// File: frontend/src/components/Dashboard/LeadScoreChart.jsx
// Distribución de lead score por banda de calidad.
//
// Antes era un doughnut de Chart.js con las bandas rotuladas 0-40 / 41-60 / 61-100,
// pero la escala real de lead_score es 0-10 (ver utils/leadScore.js): TODO lead real
// caía en "Score Baixo" y el gráfico decía que 204 de 205 leads eran malos.
//
// Forma: las bandas son ORDINALES (Baixo < Médio < Alto) y lo que se lee es
// "cuántos hay en cada una", así que son barras comparables contra una base común,
// no un doughnut (un anillo obliga a comparar ángulos y desperdicia la altura de
// la tarjeta). Cada barra lleva su etiqueta y su número: la identidad nunca depende
// sólo del color.

import React from 'react';
import { LEAD_SCORE_BANDS, LEAD_SCORE_MAX } from '../../utils/leadScore';

// Paleta validada con el validador de la skill `dataviz` (luminosidad, croma,
// separación CVD y contraste) en claro y oscuro. Ver utils/leadScore.js.
const BAND_STYLE = {
  low: { bar: 'bg-rose-600', dot: 'bg-rose-600', value: 'text-rose-600 dark:text-rose-400' },
  medium: { bar: 'bg-amber-500 dark:bg-amber-600', dot: 'bg-amber-500 dark:bg-amber-600', value: 'text-amber-600 dark:text-amber-500' },
  high: { bar: 'bg-teal-600', dot: 'bg-teal-600', value: 'text-teal-600 dark:text-teal-400' },
};

const LeadScoreChart = ({ data, avgScore }) => {
  const counts = {
    low: Number(data?.low) || 0,
    medium: Number(data?.medium) || 0,
    high: Number(data?.high) || 0,
  };
  const total = counts.low + counts.medium + counts.high;
  const avg = Number(avgScore);
  const hasAvg = Number.isFinite(avg) && avg > 0;

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-5 h-full flex flex-col">
      <div className="flex items-start justify-between mb-1">
        <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100">
          Qualidade dos Leads
        </h3>
        <span className="text-xs text-gray-500 dark:text-gray-400 whitespace-nowrap ml-2">
          {total} leads
        </span>
      </div>
      <p className="text-xs text-gray-500 dark:text-gray-400 mb-4">
        Score do SDR, escala 0–{LEAD_SCORE_MAX}
        {hasAvg && <> · média <span className="font-medium text-gray-700 dark:text-gray-300">{avg.toFixed(1)}/{LEAD_SCORE_MAX}</span></>}
      </p>

      {total > 0 ? (
        <>
          {/* Barra apilada: la composición de un vistazo. 2px de hueco entre segmentos. */}
          <div className="flex h-2 mb-5 gap-[2px]" role="presentation">
            {LEAD_SCORE_BANDS.map((band) => {
              const pct = (counts[band.key] / total) * 100;
              if (pct === 0) return null;
              return (
                <div
                  key={band.key}
                  className={`${BAND_STYLE[band.key].bar} first:rounded-l-full last:rounded-r-full`}
                  style={{ width: `${pct}%` }}
                />
              );
            })}
          </div>

          {/* Una fila por banda: etiqueta + conteo + % sobre una base común. */}
          <div className="flex-1 flex flex-col justify-around gap-4">
            {LEAD_SCORE_BANDS.map((band) => {
              const count = counts[band.key];
              const pct = (count / total) * 100;
              const style = BAND_STYLE[band.key];
              return (
                <div key={band.key}>
                  <div className="flex items-baseline justify-between mb-1.5">
                    <div className="flex items-center gap-2 min-w-0">
                      <span className={`h-2 w-2 rounded-full flex-shrink-0 ${style.dot}`} />
                      <span className="text-sm font-medium text-gray-700 dark:text-gray-200">
                        {band.label}
                      </span>
                      <span className="text-xs text-gray-400 dark:text-gray-500">{band.range}</span>
                    </div>
                    <div className="flex items-baseline gap-1.5 flex-shrink-0 ml-2">
                      <span className={`text-sm font-semibold tabular-nums ${style.value}`}>
                        {count}
                      </span>
                      <span className="text-xs text-gray-400 dark:text-gray-500 tabular-nums">
                        {pct.toFixed(0)}%
                      </span>
                    </div>
                  </div>
                  <div className="h-1.5 w-full rounded-full bg-gray-100 dark:bg-gray-700 overflow-hidden">
                    <div
                      className={`h-full rounded-full transition-all duration-300 ${style.bar}`}
                      style={{ width: `${pct}%` }}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </>
      ) : (
        <div className="flex-1 flex items-center justify-center text-center py-8">
          <div>
            <p className="text-sm text-gray-500 dark:text-gray-400">Nenhum lead com score</p>
            <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
              O bot SDR pontua os leads ao qualificá-los
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default LeadScoreChart;
